import {
  WBBox,
  WBButton,
  WBFlex,
  WBForm,
  WBSelect,
  WBSelectV2,
  WBTextField,
  WBTextFieldV2,
} from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import { CreateServiceInput, Service, TaxType } from '@admiin-com/ds-graphql';
import { Controller, useForm } from 'react-hook-form';
import { InputAdornment, MenuItem } from '@mui/material';
import { REGEX } from '@admiin-com/ds-common';
import { useSelectedEntity } from '../../hooks/useSelectedEntity/useSelectedEntity';

export interface ServiceCreateFormProps {
  entityId: string;
  defaultService?: Service | null;
  loading: boolean;
  deleting?: boolean;
  createService: (input: CreateServiceInput) => Promise<void>;
  onDelete?: () => void;
}

type CreateServerInputForm = Omit<CreateServiceInput, 'entityId'>;
export function ServiceCreateForm({
  loading,
  deleting = false,
  createService,
  entityId,
  defaultService,
  onDelete,
}: ServiceCreateFormProps) {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateServerInputForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: defaultService
      ? { ...defaultService, amount: defaultService.amount / 100 }
      : {},
  });
  const onSubmit = async (data: CreateServerInputForm) => {
    await createService({ entityId, ...data });
  };

  const { entity } = useSelectedEntity();
  const defaultTaxType = entity?.gstRegistered ? 'GST' : 'NO_GST';

  return (
    <WBForm onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="description"
        rules={{
          required: t('serviceDescriptionRequired', { ns: 'services' }),
        }}
        defaultValue=""
        render={({ field }) => (
          <WBTextFieldV2
            {...field}
            label={t('description', { ns: 'services' })}
            placeholder={t('descriptionPlaceholder', { ns: 'services' })}
            error={!!errors.description}
            helperText={errors.description?.message}
            margin="dense"
          />
        )}
      />
      <WBFlex flexDirection={['column', 'row']} alignItems={['center', 'end']}>
        <WBBox flex={1} mr={[0, 3]} width="100%">
          <Controller
            control={control}
            name="amount"
            rules={{
              validate: {
                pattern: (value: any) =>
                  REGEX.AMOUNT.test(value) ||
                  t('amountInValid', { ns: 'taskbox' }),

                greaterThanZero: (value: any) =>
                  parseFloat(value) > 0 ||
                  t('amountMustBeGreaterThanZero', { ns: 'taskbox' }),
              },
            }}
            //@ts-ignore
            defaultValue={''}
            render={({ field }) => (
              <WBTextFieldV2
                {...field}
                type="number"
                label={t('amount', { ns: 'services' })}
                placeholder={'1000.00'}
                error={!!errors.amount}
                helperText={errors.amount?.message}
                margin="dense"
                fullWidth
                leftIcon={<InputAdornment position="start">$</InputAdornment>}
              />
            )}
          />
        </WBBox>

        <WBBox flex={1} width="100%">
          <Controller
            control={control}
            name="taxType"
            rules={{ required: t('taxTypeRequired', { ns: 'services' }) }}
            //@ts-ignore
            defaultValue={defaultTaxType}
            render={({ field }) => (
              <WBSelectV2
                {...field}
                label={t('taxType', { ns: 'services' })}
                placeholder={t('taxTypePlaceholder', { ns: 'services' })}
                error={!!errors.feeType}
                helperText={errors.feeType?.message}
                margin="dense"
                fullWidth
              >
                {Object.values(TaxType)
                  .map((value) => ({
                    value,
                    label: t(value, { ns: 'common' }),
                  }))
                  .filter((value) => value.value !== TaxType.GST_INCLUSIVE)
                  .map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
              </WBSelectV2>
            )}
          />
        </WBBox>
      </WBFlex>

      <WBFlex sx={{ mt: 5 }} gap={2}>
        <WBButton loading={loading} fullWidth>
          {t(defaultService?.id ? 'update' : 'create', { ns: 'services' })}
        </WBButton>
        {defaultService && onDelete && (
          <WBButton
            variant="outlined"
            color="error"
            loading={deleting}
            onClick={onDelete}
            type="button"
            fullWidth
          >
            {t('deleteService', { ns: 'services' })}
          </WBButton>
        )}
      </WBFlex>
    </WBForm>
  );
}

export default ServiceCreateForm;
